package com.eladfinish.bible.contest.features.verseWidget

import kotlinx.serialization.Serializable

/** Where the widget's verses come from. */
enum class VerseWidgetSource {
    /** The chapters the user picked in the mikud settings. */
    MIKUD,

    /** One book of the Tanakh. */
    BOOK,

    /** The parasha of the week. */
    PARASHA,

    /** The whole Tanakh. */
    ALL,
}

enum class VerseWidgetOrder {
    RANDOM,

    /** Verse after verse, from the beginning of the book, of this week's parasha, of the Tanakh or of the mikud. */
    SEQUENTIAL,
}

/**
 * Everything the "rotating verse" widget needs to decide which verse to show, kept as one JSON string
 * in the settings. The verse on screen is a pure function of this and the time (see
 * [GetWidgetVerseUseCase]), so it can be recomputed at any moment.
 *
 * @property bookNumber 1-based number of the book in the Tanakh (28 is Mishlei), used by [VerseWidgetSource.BOOK].
 * @property showTeamim the cantillation marks, which system fonts draw badly: off unless asked for, and never without the nikud.
 * @property intervalMinutes how long a verse stays, one of [INTERVALS].
 * @property anchorEpochDay the local day (days since 1970-01-01) the rotation started on. A new
 * rhythm starts at midnight of that day, which is what makes a sequential rotation start at the
 * first verse.
 * @property phaseSeconds how far the slot boundaries lie from those of the clock, and [slotOffset] the verses
 * skipped: both are set when the user asks for a new verse (see [VerseWidgetSlots.refreshedAt]).
 * @property seed varies the random picks between users and between rotations.
 */
@Serializable
data class VerseWidgetConfig(
    val source: VerseWidgetSource = VerseWidgetSource.MIKUD,
    val bookNumber: Int = DEFAULT_BOOK_NUMBER,
    val order: VerseWidgetOrder = VerseWidgetOrder.RANDOM,
    val intervalMinutes: Int = DEFAULT_INTERVAL_MINUTES,
    val showNikud: Boolean = true,
    val showTeamim: Boolean = false,
    val anchorEpochDay: Long = 0,
    val phaseSeconds: Int = 0,
    val slotOffset: Long = 0,
    val seed: Int = 0,
) {

    /** Whether changing from [other] to this restarts the rotation (a different rhythm or content). */
    fun restartsRotationFrom(other: VerseWidgetConfig): Boolean =
        source != other.source ||
                bookNumber != other.bookNumber ||
                order != other.order ||
                intervalMinutes != other.intervalMinutes

    companion object {
        const val DEFAULT_BOOK_NUMBER = 28 // Mishlei
        const val DEFAULT_INTERVAL_MINUTES = 60
        const val FIRST_BOOK_NUMBER = 1
        const val LAST_BOOK_NUMBER = 39

        /** Every one divides a day, so a verse always changes on the same wall-clock times. */
        val INTERVALS = listOf(5, 10, 15, 30, 60, 180, 720, 1440)
    }
}
